const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://greenwich.am/api";

const ANALYTICS_ENDPOINT = `${API_BASE_URL}/analytics/track`;
const ANALYTICS_SUMMARY_ENDPOINT = `${API_BASE_URL}/analytics/summary`;
const ANALYTICS_USER_SUMMARY_ENDPOINT = `${API_BASE_URL}/analytics/user-summary`;
const ADMIN_USER_ACTIVITY_LOG_ENDPOINT = (userId) => `${API_BASE_URL}/admin/users/${userId}/activity-log`;

const CONSENT_KEY = "cookie_consent";
const PAGE_VIEW_DEDUPE_WINDOW_MS = 30 * 1000;
const MIN_DURATION_MS = 5000;

function hasAnalyticsConsent() {
  try {
    // 1) check localStorage flag
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted") return true;
    if (stored === "rejected") return false;

    // 2) fallback: check cookies
    if (typeof document !== "undefined") {
      return document.cookie.includes(`${CONSENT_KEY}=accepted`);
    }
  } catch {
    // ignore
  }
  return false;
}

// This same analytics.js is bundled unmodified into both the web build and
// the Capacitor-wrapped Android app (synced from my-app), so this one check
// is what lets the backend tell app traffic apart from web traffic — without
// it, every event defaults to "web" and app usage is invisible in analytics.
function detectPlatform() {
  if (typeof window === "undefined") return "web";
  if (window.Capacitor?.getPlatform) {
    const p = window.Capacitor.getPlatform();
    if (p === "android" || p === "ios") return p;
  }
  return "web";
}

function buildCommonPayload(user) {
  const safeUser =
    user && typeof user === "object"
      ? {
          id: user._id || user.id || user.email || null,
          role: user.role || null,
        }
      : null;

  return {
    user: safeUser,
    platform: detectPlatform(),
    timestamp: new Date().toISOString(),
  };
}

function shouldSkipShortDuration(durationMs) {
  return typeof durationMs === "number" && durationMs < MIN_DURATION_MS;
}

function shouldDedupePageView(path) {
  if (!path) return true;

  try {
    const key = `analytics:last_page_view:${path}`;
    const now = Date.now();
    const last = Number(sessionStorage.getItem(key) || 0);

    if (last && now - last < PAGE_VIEW_DEDUPE_WINDOW_MS) {
      return true;
    }

    sessionStorage.setItem(key, String(now));
  } catch {
    return false;
  }

  return false;
}

function sendWithFetch(payload) {
  if (!hasAnalyticsConsent()) return Promise.resolve();
  const token = localStorage.getItem("token");

  return fetch(ANALYTICS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  }).catch((err) => {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Analytics request failed:", err);
    }
  });
}

function sendWithBeacon(payload) {
  if (!hasAnalyticsConsent()) return Promise.resolve();
  try {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const blob = new Blob(
      [
        JSON.stringify({
          headers,
          body: payload,
        }),
      ],
      { type: "application/json" }
    );

    if (!navigator.sendBeacon(ANALYTICS_ENDPOINT, blob)) {
      return sendWithFetch(payload);
    }
  } catch {
    return sendWithFetch(payload);
  }

  return Promise.resolve();
}

export function trackPageView({ path, title, user }) {
  if (!path) return;
  if (shouldDedupePageView(path)) return Promise.resolve();

  const payload = {
    ...buildCommonPayload(user),
    type: "page_view",
    path,
    title: title || document.title || "",
  };

  return sendWithFetch(payload);
}

export function trackPageDuration({ path, durationMs, user }) {
  if (!path || typeof durationMs !== "number") return;
  if (shouldSkipShortDuration(durationMs)) return Promise.resolve();

  const payload = {
    ...buildCommonPayload(user),
    type: "page_duration",
    path,
    durationMs,
  };

  // For normal route changes we can use fetch
  return sendWithFetch(payload);
}

export function trackPageLeave({ path, durationMs, user }) {
  if (!path || typeof durationMs !== "number") return;
  if (shouldSkipShortDuration(durationMs)) return Promise.resolve();

  const payload = {
    ...buildCommonPayload(user),
    type: "page_leave",
    path,
    durationMs,
  };

  // For tab close / refresh prefer Beacon
  return sendWithBeacon(payload);
}

export function trackCustomEvent({ name, meta, user }) {
  if (!name) return;

  const payload = {
    ...buildCommonPayload(user),
    type: "custom_event",
    name,
    meta: meta || {},
  };

  return sendWithFetch(payload);
}

export async function fetchAnalyticsSummary(range = "day") {
  try {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    if (range) params.set("range", range);
    const res = await fetch(`${ANALYTICS_SUMMARY_ENDPOINT}?${params.toString()}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();

    // Flexible shape handling
    if (data.success && data.summary) return data.summary;
    if (data.data && data.data.summary) return data.data.summary;
    return data.summary || data;
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch analytics summary:", err);
    }
    return null;
  }
}

export async function fetchUserAnalyticsSummary(range = "day") {
  try {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    if (range) params.set("range", range);
    const res = await fetch(`${ANALYTICS_USER_SUMMARY_ENDPOINT}?${params.toString()}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    if (data.success && Array.isArray(data.users)) return data.users;
    if (Array.isArray(data)) return data;
    return data.users || [];
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch user analytics summary:", err);
    }
    return [];
  }
}

export async function fetchUserActivityLog(userId, { range = "month", type = "all", path = "", page = 1, limit = 25 } = {}) {
  const token = localStorage.getItem("token");
  const params = new URLSearchParams({ range, type, page: String(page), limit: String(limit) });
  if (path) params.set("path", path);

  const res = await fetch(`${ADMIN_USER_ACTIVITY_LOG_ENDPOINT(userId)}?${params.toString()}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to load activity log");
  return data;
}

